import React, {
  forwardRef,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
  type TextInputProps,
} from 'react-native';

import {
  estimateWebMultilineHeight,
  WEB_MULTILINE_MAX_H,
  WEB_MULTILINE_MIN_H,
} from './estimateWebMultilineHeight';
import { formFieldLabelText } from './formFieldLabelStyle';
import { formFieldInStack } from './formFieldStackContext';
import {
  ViewOnlyFieldValue,
  resolveIsViewOnlyPresentation,
} from './viewOnlyFieldUtils';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';
import { convertToPlainText } from '../../utils/richContent/convertToPlainText';
import { inlineImageOpenFromPress } from '../../utils/richContent/inlineImageRef';
import { AlertCircle } from '../icons';
import { AttachmentCarouselModal } from '../modals/AttachmentCarouselModal';
import { RichHtmlContent } from '../RichHtmlContent';
import { toRenderableRichHtml } from '../richHtmlContentShared';
import { ACCESSORY_VIEW_ID } from '../shared/KeyboardDismissToolbar';

import type { InlineImageOpenRequest } from '../../utils/richContent/inlineImageRef';
import type { CarouselMedia } from '../modals/AttachmentCarouselModal';

interface FormInputProps extends Omit<TextInputProps, 'style'> {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  editable?: boolean;
  readOnlyMode?: boolean;
  ignorePermissions?: boolean; // Allow bypassing permission checks for special cases
  disabled?: boolean;
  error?: string;
  /**
   * Stable id for the error text and `aria-describedby`.
   * When omitted, both stay derived from the visible label.
   */
  errorElementId?: string;
  helperText?: string;
  containerStyle?: any;
  inputStyle?: any;
  style?: any;
  labelIcon?: React.ReactNode; // Optional icon to display next to label
  hideCharacterCount?: boolean; // Hide character count even when maxLength is set
  /** When true, label row is not rendered (use a custom label outside the field). */
  hideLabel?: boolean;
  testID?: string;
  /**
   * Web only. Multiline field grows with line count and content size; ignored on iOS/Android.
   */
  webMultilineAutoSize?: boolean;
  /** Web only, when `webMultilineAutoSize`. Overrides default min/max height bounds. */
  webMultilineMinHeight?: number;
  webMultilineMaxHeight?: number;
  /** #1106 — render multiline as TextArea when section context is provided. */
  richText?: boolean;
  requestId?: string;
  requestSection?: string;
  isOnline?: boolean;
  /** Rich HTML transport key for create-flow inline image flush. */
  transportFieldKey?: string;
}

export const FormInput = forwardRef<TextInput, FormInputProps>(
  (
    {
      label,
      value,
      onChangeText,
      placeholder,
      required = false,
      multiline = false,
      editable = true,
      readOnlyMode = false,
      ignorePermissions = false,
      disabled = false,
      error,
      errorElementId,
      helperText,
      containerStyle,
      inputStyle,
      style,
      maxLength,
      labelIcon,
      hideCharacterCount = false,
      hideLabel = false,
      webMultilineAutoSize = false,
      webMultilineMinHeight: webMultilineMinHeightProp,
      webMultilineMaxHeight: webMultilineMaxHeightProp,
      testID,
      richText = false,
      requestId,
      requestSection,
      isOnline = true,
      transportFieldKey,
      onBlur,
      ...textInputProps
    },
    ref,
  ) => {
    const {
      onContentSizeChange: onContentSizeChangeUser,
      scrollEnabled: scrollEnabledProp,
      ...restTextInput
    } = textInputProps;
    const { canEditRequest } = useFormPermissions();
    const hasError = Boolean(error);
    const errorId = errorElementId ?? `${label}-error`;

    const autoMinH = webMultilineMinHeightProp ?? WEB_MULTILINE_MIN_H;
    const autoMaxH = webMultilineMaxHeightProp ?? WEB_MULTILINE_MAX_H;

    const isWebAuto =
      Platform.OS === 'web' && multiline && webMultilineAutoSize;
    const lineFromValue = useMemo(
      () =>
        isWebAuto
          ? estimateWebMultilineHeight(String(value ?? ''), {
              min: autoMinH,
              max: autoMaxH,
            })
          : 0,
      [value, isWebAuto, autoMinH, autoMaxH],
    );
    const [measuredH, setMeasuredH] = useState<number | null>(null);
    const [inlinePreview, setInlinePreview] =
      useState<InlineImageOpenRequest | null>(null);
    const openInlinePreview = useCallback((request: InlineImageOpenRequest) => {
      setInlinePreview(request);
    }, []);
    const inlinePreviewItems = useMemo((): CarouselMedia[] => {
      if (!inlinePreview) {
        return [];
      }
      return [
        {
          id: String(inlinePreview.attachmentId),
          name: inlinePreview.name,
          mimeType: 'image/*',
          previewable: true,
        },
      ];
    }, [inlinePreview]);
    const inlinePreviewModal = inlinePreview ? (
      <AttachmentCarouselModal
        visible
        onClose={() => setInlinePreview(null)}
        items={inlinePreviewItems}
        initialIndex={0}
        sectionLabel={label}
      />
    ) : null;
    const webMultilineHeight = useMemo(() => {
      if (!isWebAuto) {
        return null;
      }
      return Math.min(
        autoMaxH,
        Math.max(
          autoMinH,
          lineFromValue,
          measuredH != null && measuredH > 0 ? measuredH : 0,
        ),
      );
    }, [isWebAuto, autoMinH, autoMaxH, lineFromValue, measuredH]);

    useLayoutEffect(() => {
      if (!isWebAuto || measuredH == null) {
        return;
      }
      if (lineFromValue < measuredH - spacing.sm) {
        setMeasuredH(null);
      }
    }, [isWebAuto, lineFromValue, measuredH]);

    const onContentSizeChangeMerged = useCallback(
      (e: { nativeEvent?: { contentSize?: { height?: number } } }) => {
        if (isWebAuto) {
          const h = e?.nativeEvent?.contentSize?.height;
          if (typeof h === 'number' && h > 0) {
            /**
             * Use reported content height only — the character count row is a sibling below the
             * `TextInput`, not inside the bordered box; adding slack here stacked visually with
             * `charCountRow` margin/padding and looked like double spacing.
             */
            setMeasuredH(Math.min(autoMaxH, Math.max(autoMinH, h)));
          }
        }
        onContentSizeChangeUser?.(e as any);
      },
      [isWebAuto, autoMaxH, autoMinH, onContentSizeChangeUser],
    );

    const isEditable = ignorePermissions
      ? editable
      : editable && canEditRequest;
    const isPermissionLocked = !disabled && !readOnlyMode && !isEditable;
    const isDisabled = disabled || readOnlyMode || isPermissionLocked;
    const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
      readOnlyMode,
      disabled,
      editable,
      canEditRequest,
      ignorePermissions,
    });
    const isReadOnlyInput =
      isDisabled && (readOnlyMode || editable === false || isPermissionLocked);
    const isReadOnlyMultiline = multiline && isReadOnlyInput;
    const isReadOnlySingleLine = !multiline && isReadOnlyInput;
    const isDisabledMultiline = multiline && isDisabled && !isReadOnlyMultiline;
    const showCharCount =
      maxLength && maxLength > 0 && !hideCharacterCount && !isDisabled;
    const plainTextLength = richText
      ? convertToPlainText(value).length
      : value.length;
    const useRichTextEditor =
      multiline &&
      richText &&
      Boolean(requestSection) &&
      typeof maxLength === 'number' &&
      maxLength > 0;

    if (isViewOnlyPresentation) {
      if (multiline && (richText || /<img\b/i.test(value))) {
        return (
          <View
            style={[containerStyle, style, formFieldInStack]}
            testID={testID ?? 'form-input-container'}
          >
            {!hideLabel ? (
              <View style={styles.labelContainer}>
                <View style={styles.labelTextWrap}>
                  <Text style={styles.label}>
                    {label}
                    {required && <Text style={styles.required}> *</Text>}
                  </Text>
                </View>
                {labelIcon ? (
                  <View style={styles.labelIconContainer}>{labelIcon}</View>
                ) : null}
              </View>
            ) : null}
            <RichHtmlContent
              value={value}
              requestId={requestId}
              testID={testID ? `${testID}-readonly` : undefined}
              viewField
              onImagePress={(src, ordinal) => {
                const request = inlineImageOpenFromPress(
                  toRenderableRichHtml(value),
                  src,
                  ordinal,
                );
                if (request) {
                  openInlinePreview(request);
                }
              }}
            />
            {inlinePreviewModal}
            {error ? (
              <View
                style={styles.errorRow}
                accessibilityRole="text"
                accessibilityLabel={error}
              >
                <AlertCircle
                  size={14}
                  color={colors.error}
                  style={styles.errorRowIcon}
                />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
            {helperText && !error ? (
              <Text style={styles.helperText}>{helperText}</Text>
            ) : null}
          </View>
        );
      }
      return (
        <ViewOnlyFieldValue
          label={label}
          value={value}
          hideLabel={hideLabel}
          multiline={multiline}
          testID={testID ?? 'form-input-container'}
          valueTestID={testID ? `${testID}-value` : 'view-only-field-value'}
          containerStyle={[containerStyle, style, formFieldInStack]}
          error={error}
          helperText={helperText}
        />
      );
    }

    if (useRichTextEditor) {
      const { TextArea } =
        require('../textArea/TextArea') as typeof import('../textArea/TextArea');
      return (
        <View
          style={[styles.formGroup, containerStyle, style, formFieldInStack]}
        >
          <TextArea
            label={label}
            value={value}
            onChange={onChangeText}
            maxLength={maxLength}
            fieldLabel={label}
            requestId={requestId}
            isOnline={isOnline}
            requestSection={requestSection!}
            placeholder={placeholder}
            error={error}
            disabled={isDisabled}
            hideLabel={hideLabel}
            testID={testID}
            containerStyle={undefined}
            transportFieldKey={transportFieldKey}
            onOpenInlineImage={openInlinePreview}
          />
          {inlinePreviewModal}
          {helperText && !error ? (
            <Text style={styles.helperText}>{helperText}</Text>
          ) : null}
        </View>
      );
    }

    return (
      <View
        style={[styles.formGroup, containerStyle, style, formFieldInStack]}
        testID={testID ?? 'form-input-container'}
      >
        {!hideLabel ? (
          <View style={styles.labelContainer}>
            <View style={styles.labelTextWrap}>
              <Text style={styles.label}>
                {label}
                {required && <Text style={styles.required}> *</Text>}
              </Text>
            </View>
            {labelIcon ? (
              <View style={styles.labelIconContainer}>{labelIcon}</View>
            ) : null}
          </View>
        ) : null}
        {/*
        Placeholder colour uses different theme tokens for disabled vs enabled:

        - Enabled → colors.inputPlaceholder (#999): hints stay visually weaker than real typed values.
        - Disabled → colors.textFormDisabled: aligns placeholder with Figma disabled tokens.

        Tests: FormInput.test.tsx ("placeholderTextColor").
      */}
        <TextInput
          key={Platform.OS === 'ios' ? `input-${isDisabled}` : undefined}
          ref={ref}
          returnKeyType={multiline ? 'default' : 'done'}
          inputAccessoryViewID={
            Platform.OS === 'ios' && !isDisabled && multiline && !richText
              ? ACCESSORY_VIEW_ID
              : undefined
          }
          style={[
            styles.input,
            !multiline && {
              height: spacing.formControlHeight,
              maxHeight: spacing.formControlHeight,
            },
            Platform.OS === 'web' && !multiline && styles.inputWebSingleLine,
            multiline && styles.multilinePadding,
            multiline &&
              Platform.OS === 'web' &&
              !isDisabled &&
              styles.multilineWebField,
            multiline && !isWebAuto && styles.multilineInput,
            isWebAuto && styles.multilineInputWebAuto,
            isWebAuto &&
              Platform.OS === 'web' && {
                minHeight: autoMinH,
                maxHeight: autoMaxH,
              },
            isWebAuto &&
              webMultilineHeight != null && { minHeight: webMultilineHeight },
            isDisabled &&
              !multiline &&
              !isReadOnlySingleLine &&
              styles.disabledInput,
            isReadOnlySingleLine && styles.readOnlyInput,
            isReadOnlySingleLine &&
              Platform.OS === 'web' && {
                WebkitTextFillColor: colors.textPrimary,
              },
            isDisabledMultiline && styles.disabledMultiline,
            isReadOnlyMultiline && styles.readOnlyMultiline,
            isReadOnlyMultiline &&
              Platform.OS === 'web' && {
                WebkitTextFillColor: colors.textPrimary,
              },
            hasError && styles.errorInput,
            hasError && multiline && styles.multilineErrorField,
            inputStyle,
          ]}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          multiline={multiline}
          editable={!isDisabled}
          scrollEnabled={multiline && isDisabled ? true : scrollEnabledProp}
          maxLength={maxLength}
          placeholderTextColor={
            disabled && !isReadOnlyInput
              ? colors.textFormDisabled
              : colors.inputPlaceholder
          }
          accessibilityLabel={label}
          accessibilityHint={helperText || placeholder}
          accessibilityRequired={required}
          {...Platform.select({
            web: {
              'aria-label': label,
              'aria-required': required,
              'aria-invalid': hasError,
              'aria-describedby': error
                ? errorId
                : helperText
                ? `${label}-helper`
                : undefined,
              ...(isDisabled ? { readOnly: true as const } : {}),
            },
          })}
          onContentSizeChange={onContentSizeChangeMerged}
          onBlur={onBlur}
          {...restTextInput}
        />
        {showCharCount && (
          <View style={styles.charCountRow} testID="form-input-char-count">
            <Text
              style={styles.charCount}
              accessibilityLabel={`${plainTextLength} of ${maxLength} characters`}
            >
              {plainTextLength} / {maxLength}
            </Text>
          </View>
        )}
        {error && (
          <View
            style={styles.errorRow}
            accessibilityRole="text"
            accessibilityLabel={error}
          >
            <AlertCircle
              size={14}
              color={colors.error}
              style={styles.errorRowIcon}
            />
            <Text
              style={styles.errorText}
              nativeID={errorElementId}
              {...Platform.select({
                web: { id: errorId },
              })}
            >
              {error}
            </Text>
          </View>
        )}
        {helperText && !error && (
          <Text
            style={styles.helperText}
            {...Platform.select({
              web: { id: `${label}-helper` },
            })}
          >
            {helperText}
          </Text>
        )}
      </View>
    );
  },
);

FormInput.displayName = 'FormInput';

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 0,
    ...Platform.select({
      web: {
        minWidth: 0,
        maxWidth: '100%' as const,
        alignSelf: 'stretch' as const,
      },
      default: {},
    }),
  },
  labelContainer: {
    width: '100%' as const,
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    justifyContent: 'flex-start' as const,
    marginBottom: spacing.fieldVerticalGap,
  },
  /** Shrink-wrapped so `labelIcon` sits after the text, not at the far right of the field. */
  labelTextWrap: {
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%' as const,
    ...Platform.select({
      web: { maxWidth: 'calc(100% - 32px)' as const },
      default: {},
    }),
  },
  label: {
    ...formFieldLabelText,
  },
  labelIconContainer: {
    marginLeft: spacing.formLabelIconMarginLeft,
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  required: {
    color: colors.error,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    backgroundColor: colors.backgroundForm,
    color: colors.textPrimary,
    minHeight: spacing.formControlHeight,
    paddingVertical: spacing.formControlSingleLinePaddingVertical,
    ...Platform.select({
      web: {
        width: '100%' as const,
        minWidth: 0,
        maxWidth: '100%' as const,
        outlineStyle: 'none' as const,
        outlineWidth: 0,
        boxSizing: 'border-box' as const,
        WebkitAppearance: 'none' as const,
        /** RN Web may not apply `placeholderTextColor`; keep typed value `color` above (textPrimary). */
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
      },
    }),
  },
  /**
   * Single-line web inputs: hover darkens the grey border; keyboard focus switches to **brand orange**
   * plus the soft shadow from `formControlFocusRing`. Multiline uses {@link multilineWebField} so both
   * control types feel consistent (we previously matched template blue focus on textarea only—users saw a mismatch).
   */
  inputWebSingleLine: {
    ...Platform.select({
      web: {
        ':focus': {
          outlineWidth: 0,
          borderColor: colors.inputBorderFocus,
          boxShadow: colors.formControlFocusRing,
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
      default: {},
    }),
  },
  /** Figma Text area (`2432:6408`): `--blur/md` vertical padding 12px (single-line stays 11px / 48px control). */
  multilinePadding: {
    paddingVertical: spacing.formTextAreaPaddingVertical,
  },
  /**
   * Web **multiline** inputs — mirrors single-line behaviour so a long text box doesn’t look like a
   * different component family:
   * - `:hover` → lighter grey stroke (`inputBorderInteractive`)
   * - `:focus` → thicker orange border + same glow as single-line (`formControlFocusRing`)
   *
   * Without this, designers/testing flagged “why does the big box turn blue but small boxes turn orange?”
   */
  multilineWebField: {
    ...Platform.select({
      web: {
        ':hover': {
          borderColor: colors.inputBorderInteractive,
          borderWidth: 1,
        },
        ':focus': {
          outlineWidth: 0,
          borderColor: colors.inputBorderFocus,
          borderWidth: 2,
          boxShadow: colors.formControlFocusRing,
        },
      },
      default: {},
    }),
  },
  /** iOS/Android: fixed height. Web: min/max height with internal scroll + vertical resize handle. */
  multilineInput: {
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        minHeight: spacing.formTextAreaMinHeightWeb,
        maxHeight: spacing.formTextAreaMaxHeightWeb,
        resize: 'vertical' as const,
        overflow: 'auto' as const,
      },
      default: {
        minHeight: spacing.formTextAreaMinHeightNative,
        height: spacing.formTextAreaMinHeightNative,
      },
    }),
  },
  multilineInputWebAuto: {
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        resize: 'vertical' as const,
        overflow: 'auto' as const,
      },
    }),
  },
  disabledInput: {
    backgroundColor: colors.backgroundSecondary,
    /** Align disabled value tone with Visual Template / `FormDropdown` (`textFormDisabled`). */
    color: colors.textFormDisabled,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
        '::placeholder': {
          color: colors.textFormDisabled,
          opacity: 1,
        },
      },
    }),
  },
  readOnlyInput: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    color: colors.textPrimary,
    ...Platform.select({
      web: {
        cursor: 'default' as const,
        backgroundColor: colors.backgroundFormDisabled,
        color: colors.textPrimary,
        opacity: 1,
        borderColor: colors.inputBorderDisabled,
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
        ':hover': {
          borderColor: colors.inputBorderDisabled,
          borderWidth: 1,
          boxShadow: 'none',
        },
        ':focus': {
          borderColor: colors.inputBorderDisabled,
          borderWidth: 1,
          boxShadow: 'none',
          outlineWidth: 0,
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  /**
   * Figma Text area disabled (`2432:10110`): grey fill, muted value,
   * neutral border, internal scroll — not the single-line disabled chrome.
   */
  disabledMultiline: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    color: colors.textFormDisabled,
    ...Platform.select({
      web: {
        cursor: 'default' as const,
        overflow: 'auto' as const,
        resize: 'none' as const,
        backgroundColor: colors.backgroundFormDisabled,
        color: colors.textFormDisabled,
        borderColor: colors.inputBorderDisabled,
        '::placeholder': {
          color: colors.textFormDisabled,
          opacity: 1,
        },
        ':hover': {
          borderColor: colors.inputBorderDisabled,
          borderWidth: 1,
          boxShadow: 'none',
        },
        ':focus': {
          borderColor: colors.inputBorderDisabled,
          borderWidth: 1,
          boxShadow: 'none',
          outlineWidth: 0,
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  readOnlyMultiline: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    color: colors.textPrimary,
    paddingBottom: spacing.formTextAreaPaddingVertical,
    ...Platform.select({
      web: {
        cursor: 'default' as const,
        overflow: 'auto' as const,
        resize: 'none' as const,
        backgroundColor: colors.backgroundFormDisabled,
        color: colors.textPrimary,
        opacity: 1,
        borderColor: colors.inputBorderDisabled,
        paddingBottom: spacing.formTextAreaPaddingVertical,
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
        ':hover': {
          borderColor: colors.inputBorderDisabled,
          borderWidth: 1,
          boxShadow: 'none',
        },
        ':focus': {
          borderColor: colors.inputBorderDisabled,
          borderWidth: 1,
          boxShadow: 'none',
          outlineWidth: 0,
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  errorInput: {
    borderColor: colors.inputBorderCritical,
  },
  multilineErrorField: {
    borderColor: colors.inputBorderCritical,
    ...Platform.select({
      web: {
        ':hover': {
          borderColor: colors.inputBorderCritical,
        },
        ':focus': {
          borderColor: colors.inputBorderCritical,
          boxShadow: 'none',
        },
      },
      default: {},
    }),
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: spacing.xs,
    gap: spacing.formInlineFeedbackGap,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    fontSize: fontSize.charCount,
    color: colors.error,
    flex: 1,
  },
  helperText: {
    fontSize: fontSize.charCount,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  /**
   * Full-width row so the count is right-aligned inside the field band, not on an intrinsic-
   * width Text (avoids clipping at card edges on web). No bottom padding here — `FormFieldStack`
   * `gap` / `formGroup` margin supplies one step to the next field; extra padding stacked with
   * that gap (notably on web) read as double spacing under the counter.
   */
  charCountRow: {
    width: '100%' as const,
    minWidth: 0,
    marginTop: spacing.charCountMarginTop,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    ...Platform.select({
      web: {
        boxSizing: 'border-box' as const,
        paddingRight: 0,
      },
      default: {
        paddingRight: 0,
      },
    }),
  },
  charCount: {
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.charCount,
    color: colors.textSecondary,
  },
});
